"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Building2, House as Home, Trees, ClipboardCheck, ShieldCheck, FileCheck2, ArrowRight, HardHat, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const bracknellEnvironments = [{
  title: "Town-centre commercial",
  description: "Pedestrian-safe scaffolding with debris netting, fan protection, and out-of-hours erection around busy retail and office districts.",
  icon: Building2,
  badge: "Commercial",
  highlights: "Pavement licences, public protection fans, alarmed access ladders"
}, {
  title: "New-build residential estates",
  description: "Multi-plot progressive access, birdcages, and edge protection aligned to build schedules across expanding development parcels.",
  icon: Home,
  badge: "Developers",
  highlights: "Phased plot handovers, TG20 compliant builds, loading bays"
}, {
  title: "Established suburban areas",
  description: "Tailored domestic access for re-roofing, solar arrays, and chimney work on detached and semi-detached homes with neat site footprints.",
  icon: Trees,
  badge: "Residential & Roofing",
  highlights: "Driveway protection, tidy dismantling, conservatory bridging"
}];
const safetyWorkflow = [{
  step: "01",
  title: "Site assessment & design",
  description: "Ground conditions, public interaction, building loading, and clear access routes are mapped before any equipment arrives on site.",
  icon: HardHat
}, {
  step: "02",
  title: "Erection & safe access",
  description: "Erected by qualified scaffolders with double guardrails, kickboards, secured ladder access, and required containment.",
  icon: ShieldCheck
}, {
  step: "03",
  title: "Handover & statutory inspections",
  description: "Formal handover certificate issued on completion, with documented weekly 7-day safety inspections throughout hire.",
  icon: ClipboardCheck
}];
const accreditations = [{
  name: "NASC",
  fullName: "National Access & Scaffolding Confederation",
  detail: "Audited member adhering to rigorous technical guidance and safety management criteria."
}, {
  name: "CHAS",
  fullName: "Contractors Health and Safety Assessment Scheme",
  detail: "Accredited contractor demonstrating compliance with UK health and safety legislation."
}];
export default function BracknellAccess() {
  const containerRef = useRef<HTMLElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".reveal-item", {
        y: 30
      });
      gsap.to(".reveal-item", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.1,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".reveal-item", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready?.then(refresh);
  }, {
    scope: containerRef
  });
  return <section ref={containerRef} id="bracknellaccesssafety" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="reveal-item max-w-3xl mb-12 md:mb-16" style={{
        opacity: 0
      }}>
          <Badge variant="outline" className="mb-3 uppercase tracking-wider text-xs font-semibold">
            Local access expertise
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
            Scaffolding built for Bracknell&apos;s exact sites
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            From town-centre commercial schemes to new-build housing plots and quiet suburban street works, our scaffolding provides safe, audited access for contractors, trades, and homeowners across the Bracknell area.
          </p>
        </div>

        {/* Bracknell Site Contexts */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {bracknellEnvironments.map((env, i) => {
          const IconComponent = env.icon;
          return <div key={env.title} data-index={i} className="reveal-item h-full" style={{
            opacity: 0
          }}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="w-10 h-10 rounded-md bg-accent flex items-center justify-center text-primary">
                        <IconComponent className="size-5" />
                      </div>
                      <Badge variant="secondary" className="text-xs">
                        {env.badge}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-semibold">
                      {env.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm pt-1">
                      {env.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <Separator className="my-4" />
                    <div className="text-xs text-muted-foreground">
                      <span className="font-medium text-foreground block mb-1">Key safety controls:</span>
                      {env.highlights}
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        {/* Safety & Compliance Section */}
        <div className="reveal-item bg-muted/40 rounded-xl p-6 sm:p-8 md:p-10 border border-border mb-16" style={{
        opacity: 0
      }}>
          <div className="max-w-2xl mb-8">
            <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm mb-2">
              <ShieldCheck className="size-4" />
              <span>Three-stage safety process</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight">
              Safety managed from initial survey to final dismantle
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {safetyWorkflow.map((item, i) => {
            const StepIcon = item.icon;
            return <div key={item.step} data-index={i} className="bg-card rounded-lg p-6 border border-border/80 shadow-xs flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-mono font-bold px-2 py-1 bg-accent rounded text-foreground">
                        {item.step}
                      </span>
                      <StepIcon className="size-5 text-primary" />
                    </div>
                    <h4 className="text-lg font-semibold mb-2">{item.title}</h4>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Accreditations & CTA Banner */}
        <div className="reveal-item grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-card rounded-xl border border-border p-6 sm:p-8 md:p-10 shadow-sm" style={{
        opacity: 0
      }}>
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary uppercase tracking-wider mb-2">
              <FileCheck2 className="size-4" />
              <span>Verified Standards</span>
            </div>
            <h3 className="text-2xl font-bold mb-3">
              Audited by industry safety bodies
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base mb-6">
              BMF Scaffolding holds verified membership and certifications with national bodies, providing assurance for principal contractors, surveyors, and domestic clients.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {accreditations.map((acc, i) => <div key={acc.name} data-index={i} className="flex items-start gap-3 bg-accent/40 rounded-lg p-3.5 border border-border/60">
                  <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-foreground text-sm flex items-center gap-1.5">
                      <span>{acc.name}</span>
                      <span className="text-xs font-normal text-muted-foreground">Certified</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                      {acc.fullName}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>

          <div className="lg:col-span-5 bg-accent/30 rounded-lg p-6 border border-border/80 flex flex-col justify-center items-start">
            <h4 className="font-semibold text-base mb-2">
              Planning a project in Bracknell?
            </h4>
            <p className="text-xs sm:text-sm text-muted-foreground mb-5 leading-relaxed">
              Contact our team for a free site assessment and transparent, fixed-scope scaffolding proposal.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <Button asChild className="w-full sm:w-auto">
                <Link to="/ContactRequestQuotePage">
                  <span>Request a quote</span>
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to="/HealthSafetyAccreditationsPage">
                  <span>View accreditations</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}